
import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamMetricsFilteredByTableQuery,
  DatabaseDownstreamMetricsFilteredByTableQuery_tables_downstreamMetricsConnection_nodes,
  DatabaseDownstreamMetricsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamMetricsFilteredByTableQuery';
import {
  DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQuery,
  DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQuery_tables_downstreamMetricsConnection_nodes,
  DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQuery';
import {
  DatabaseDownstreamMetricsQuery,
  DatabaseDownstreamMetricsQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamMetricsQuery';
import {
  DatabaseDownstreamMetricsWithoutPersonalSpaceQuery,
  DatabaseDownstreamMetricsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamMetricsWithoutPersonalSpaceQuery';
import { MetricsDisplayGridRowDataFragment } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragment';
import { MetricsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/MetricsDisplayGridRowDataFragmentWithoutPersonalSpace';
import { MetricSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatabaseDownstreamMetricsFilteredByTableQueryGql } from '../../queries/DatabaseDownstreamMetricsFilteredByTableQueryGql';
import {
  DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQueryGql,
} from '../../queries/DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQueryGql';
import { DatabaseDownstreamMetricsQueryGql } from '../../queries/DatabaseDownstreamMetricsQueryGql';
import { DatabaseDownstreamMetricsWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseDownstreamMetricsWithoutPersonalSpaceQueryGql';
import { getRowDataForMetric, getRowDataForMetricWithoutPersonalSpace, MetricsDisplayGrid, MetricsDisplayGridRowData } from '../sharedDisplayGrid/MetricsDisplayGrid';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

type QueryVariables = DatabaseDownstreamMetricsQueryVariables;
type QueryResults = DatabaseDownstreamMetricsQuery;
type QueryResultListNode = MetricsDisplayGridRowDataFragment;
type GridRowData = MetricsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatabaseDownstreamMetricsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatabaseDownstreamMetricsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = MetricsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = DatabaseDownstreamMetricsFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamMetricsFilteredByTableQuery;
type QueryResultListNodeWithFilter = DatabaseDownstreamMetricsFilteredByTableQuery_tables_downstreamMetricsConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQuery;
type QueryResultListNodeWithFilterWithoutPersonalSpace =
  DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQuery_tables_downstreamMetricsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData?.databases[0]?.downstreamMetricsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData?.databases[0]?.downstreamMetricsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  return resultData?.tables[0]?.downstreamMetricsConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  return resultData?.tables[0]?.downstreamMetricsConnection;
}

export type DatabaseDownstreamMetricsQueryGridProps =
  GridSortProps<MetricsValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamMetricsQueryGrid(props: DatabaseDownstreamMetricsQueryGridProps): JSX.Element {

  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamMetricsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredMetricsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
        queryGql={DatabaseDownstreamMetricsFilteredByTableQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForMetric}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
          queryGql={DatabaseDownstreamMetricsQueryGql}
          partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResult}
          mapResultNode={getRowDataForMetric}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithFilterWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseDownstreamMetricsFilteredByTableWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as MetricSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForMetricWithoutPersonalSpace}
      >
        {({ rowStore }) => <MetricsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
        <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
          queryGql={DatabaseDownstreamMetricsWithoutPersonalSpaceQueryGql}
          partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as MetricSortOrder }}
          selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
          mapResultNode={getRowDataForMetricWithoutPersonalSpace}
        >
          {({ rowStore }) => <MetricsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
        </PaginatedQueryWrapper>
    )
  );
}
